import { useState } from 'react';
import { formatCurrency } from '../utils/mortgageCalculations';

function AmortizationSchedule({ schedule, loanType, viewMode, setViewMode, selectedYear, setSelectedYear, escrow, pmi }) {
    // State lifted to App.jsx for persistence

    // Group by year for yearly view
    const yearlyData = schedule.reduce((acc, payment) => {
        if (!acc[payment.year]) {
            acc[payment.year] = {
                year: payment.year,
                totalPayment: 0,
                totalPrincipal: 0,
                totalInterest: 0,
                totalPMI: 0,
                endingBalance: 0,
                hasArmWarning: false
            };
        }
        acc[payment.year].totalPayment += payment.payment;
        acc[payment.year].totalPrincipal += payment.principal;
        acc[payment.year].totalInterest += payment.interest;
        acc[payment.year].totalPMI += (payment.pmi || 0);
        acc[payment.year].endingBalance = payment.balance;
        if (payment.isArmWarning) {
            acc[payment.year].hasArmWarning = true;
        }
        return acc;
    }, {});

    const yearlySchedule = Object.values(yearlyData);
    const monthlyScheduleForYear = schedule.filter(p => p.year === selectedYear);

    const armYears = {
        '5-1-arm': 5,
        '7-1-arm': 7,
        '10-1-arm': 10
    };

    const armFixedYears = armYears[loanType];

    const totalInterest = schedule.reduce((sum, p) => sum + p.interest, 0);
    const totalPMI = schedule.reduce((sum, p) => sum + (p.pmi || 0), 0);

    // Total Loan Paid = P&I + PMI only (from schedule)
    const totalLoanPaid = schedule.reduce((sum, p) => sum + p.payment, 0);

    // Total Paid = P&I + PMI + escrow (property tax + insurance)
    // escrow includes PMI, so we need to subtract PMI to get just property tax + insurance
    const escrowWithoutPMI = escrow - pmi;
    const totalPaid = totalLoanPaid + (escrowWithoutPMI * schedule.length);

    return (
        <div className="card">
            <div className="card-header">
                <h3 className="card-title">Amortization Schedule</h3>
                <div className="flex gap-2">
                    <button
                        className={`btn ${viewMode === 'yearly' ? 'btn-primary' : 'btn-secondary'}`}
                        onClick={() => setViewMode('yearly')}
                    >
                        Yearly View
                    </button>
                    <button
                        className={`btn ${viewMode === 'monthly' ? 'btn-primary' : 'btn-secondary'}`}
                        onClick={() => setViewMode('monthly')}
                    >
                        Monthly View
                    </button>
                </div>
            </div>

            {armFixedYears && (
                <div className="alert alert-warning">
                    <strong>ARM Loan Notice:</strong> This is a {armFixedYears}/1 ARM loan.
                    The interest rate is fixed for the first {armFixedYears} years, after which it may adjust annually.
                    The amortization schedule shown uses the initial rate for all years. Actual payments may vary after year {armFixedYears}.
                </div>
            )}

            {viewMode === 'yearly' ? (
                <div className="table-container">
                    <table style={{ width: '100%' }}>
                        <thead>
                            <tr>
                                <th>Year</th>
                                <th>Total Paid</th>
                                <th>Principal Paid</th>
                                <th>Interest Paid</th>
                                <th>PMI Paid</th>
                                <th>Remaining Balance</th>
                            </tr>
                        </thead>
                        <tbody>
                            {yearlySchedule.map((year) => (
                                <tr
                                    key={year.year}
                                    className={year.hasArmWarning ? 'warning' : ''}
                                >
                                    <td>
                                        <strong>Year {year.year}</strong>
                                        {year.hasArmWarning && <span className="badge badge-warning" style={{ marginLeft: '0.5rem' }}>ARM Adjustable</span>}
                                    </td>
                                    <td>{formatCurrency(year.totalPayment)}</td>
                                    <td>{formatCurrency(year.totalPrincipal)}</td>
                                    <td>{formatCurrency(year.totalInterest)}</td>
                                    <td>{formatCurrency(year.totalPMI)}</td>
                                    <td>{formatCurrency(year.endingBalance)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : (
                <>
                    <div className="form-group">
                        <label className="form-label">Select Year</label>
                        <select
                            className="form-select"
                            value={selectedYear}
                            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                        >
                            {yearlySchedule.map(year => (
                                <option key={year.year} value={year.year}>
                                    Year {year.year}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="table-container" style={{ maxHeight: '600px', overflowY: 'auto' }}>
                        <table style={{ width: '100%' }}>
                            <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                                <tr>
                                    <th>Month</th>
                                    <th>Payment</th>
                                    <th>Principal</th>
                                    <th>Interest</th>
                                    <th>PMI</th>
                                    <th>Balance</th>
                                </tr>
                            </thead>
                            <tbody>
                                {monthlyScheduleForYear.map((payment) => (
                                    <tr key={payment.month} className={payment.isArmWarning ? 'warning' : ''}>
                                        <td>{payment.month}</td>
                                        <td>{formatCurrency(payment.payment)}</td>
                                        <td>{formatCurrency(payment.principal)}</td>
                                        <td>{formatCurrency(payment.interest)}</td>
                                        <td>{formatCurrency(payment.pmi || 0)}</td>
                                        <td>{formatCurrency(payment.balance)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}

            {/* Summary Statistics */}
            <div className="summary-grid" style={{ marginTop: 'var(--spacing-md)', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
                <div className="summary-card">
                    <div className="summary-label">Total Loan Paid</div>
                    <div className="summary-value">
                        {formatCurrency(totalLoanPaid)}
                    </div>
                    <div className="summary-subtext">P&I + PMI only</div>
                </div>
                <div className="summary-card">
                    <div className="summary-label">Total Paid (w/ Escrow)</div>
                    <div className="summary-value">
                        {formatCurrency(totalPaid)}
                    </div>
                    <div className="summary-subtext">Includes property tax & insurance</div>
                </div>
                <div className="summary-card">
                    <div className="summary-label">Total Interest Paid</div>
                    <div className="summary-value">
                        {formatCurrency(totalInterest)}
                    </div>
                    <div className="summary-subtext">
                        {((totalInterest / totalLoanPaid) * 100).toFixed(1)}% of loan total
                    </div>
                </div>
                <div className="summary-card">
                    <div className="summary-label">Total PMI Paid</div>
                    <div className="summary-value">
                        {formatCurrency(totalPMI)}
                    </div>
                    <div className="summary-subtext">
                        {totalLoanPaid > 0 ? ((totalPMI / totalLoanPaid) * 100).toFixed(1) : 0}% of loan total
                    </div>
                </div>
            </div>
        </div>
    );
}

export default AmortizationSchedule;
